import { useReducer, useCallback, useMemo } from 'react';
import { ConversationState, ConversationAction, ChatMessage, Recommendation, ToolCallMessage } from '../types';

const generateMessageId = (): string => {
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
};

const initialState: ConversationState = {
  messages: [],
  isStreaming: false,
  error: null,
  pendingToolCalls: [],
};

const conversationReducer = (state: ConversationState, action: ConversationAction): ConversationState => {
  switch (action.type) {
    case 'ADD_USER_MESSAGE': {
      const newMessage: ChatMessage = {
        id: generateMessageId(),
        role: 'user',
        content: action.payload.content,
        timestamp: Date.now(),
      };
      return {
        ...state,
        messages: [...state.messages, newMessage],
        error: null,
      };
    }

    case 'START_STREAMING': {
      const assistantMessage: ChatMessage = {
        id: action.payload.messageId,
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
        isStreaming: true,
      };
      return {
        ...state,
        messages: [...state.messages, assistantMessage],
        isStreaming: true,
        error: null,
      };
    }

    case 'APPEND_TOKEN': {
      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === action.payload.messageId ? { ...msg, content: msg.content + action.payload.token } : msg,
        ),
      };
    }

    case 'ADD_TOOL_CALL': {
      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === action.payload.messageId
            ? {
                ...msg,
                toolCalls: [...(msg.toolCalls || []), action.payload.toolCall],
              }
            : msg,
        ),
        pendingToolCalls: [...state.pendingToolCalls, action.payload.toolCall],
      };
    }

    case 'ADD_TOOL_RESULT': {
      const toolMessage: ChatMessage = {
        id: generateMessageId(),
        role: 'tool',
        content: JSON.stringify(action.payload.toolResult.result),
        timestamp: Date.now(),
        toolResult: action.payload.toolResult,
      };
      return {
        ...state,
        messages: [...state.messages, toolMessage],
        pendingToolCalls: state.pendingToolCalls.filter((tc) => tc.id !== action.payload.toolResult.toolCallId),
      };
    }

    case 'COMPLETE_STREAMING': {
      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === action.payload.messageId
            ? {
                ...msg,
                isStreaming: false,
                recommendations: action.payload.recommendations,
              }
            : msg,
        ),
        isStreaming: false,
        pendingToolCalls: [],
      };
    }

    case 'CLEAR_MESSAGE_CONTENT': {
      // Clear the content of a message (used when continuing after tool calls)
      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === action.payload.messageId
            ? {
                ...msg,
                content: '',
                toolCalls: undefined, // Clear tool calls as they've been processed
              }
            : msg,
        ),
      };
    }

    case 'SET_ERROR': {
      return {
        ...state,
        error: action.payload.error,
        isStreaming: false,
        pendingToolCalls: [],
      };
    }

    case 'CLEAR_ERROR': {
      return {
        ...state,
        error: null,
      };
    }

    case 'CLEAR_HISTORY': {
      return {
        ...initialState,
      };
    }

    case 'LOAD_MESSAGES': {
      return {
        ...state,
        messages: action.payload.messages,
      };
    }

    default:
      return state;
  }
};

interface UseChatStateReturn {
  state: ConversationState;
  addUserMessage: (content: string) => string;
  startStreaming: (messageId: string) => void;
  appendToken: (messageId: string, token: string) => void;
  addToolCall: (messageId: string, toolCall: ToolCallMessage) => void;
  addToolResult: (toolResult: ChatMessage['toolResult']) => void;
  completeStreaming: (messageId: string, recommendations?: Recommendation[]) => void;
  clearMessageContent: (messageId: string) => void;
  setError: (error: string) => void;
  clearError: () => void;
  clearHistory: () => void;
  loadMessages: (messages: ChatMessage[]) => void;
  getMessagesForLLM: () => ChatMessage[];
}

export const useChatState = (): UseChatStateReturn => {
  const [state, dispatch] = useReducer(conversationReducer, initialState);

  const addUserMessage = useCallback((content: string): string => {
    const messageId = generateMessageId();
    dispatch({
      type: 'ADD_USER_MESSAGE',
      payload: { content },
    });
    return messageId;
  }, []);

  const startStreaming = useCallback((messageId: string): void => {
    dispatch({
      type: 'START_STREAMING',
      payload: { messageId },
    });
  }, []);

  const appendToken = useCallback((messageId: string, token: string): void => {
    dispatch({
      type: 'APPEND_TOKEN',
      payload: { messageId, token },
    });
  }, []);

  const addToolCall = useCallback((messageId: string, toolCall: ToolCallMessage): void => {
    dispatch({
      type: 'ADD_TOOL_CALL',
      payload: { messageId, toolCall },
    });
  }, []);

  const addToolResult = useCallback((toolResult: ChatMessage['toolResult']): void => {
    if (toolResult) {
      dispatch({
        type: 'ADD_TOOL_RESULT',
        payload: { toolResult },
      });
    }
  }, []);

  const completeStreaming = useCallback((messageId: string, recommendations?: Recommendation[]): void => {
    dispatch({
      type: 'COMPLETE_STREAMING',
      payload: { messageId, recommendations },
    });
  }, []);

  const clearMessageContent = useCallback((messageId: string): void => {
    dispatch({
      type: 'CLEAR_MESSAGE_CONTENT',
      payload: { messageId },
    });
  }, []);

  const setError = useCallback((error: string): void => {
    dispatch({
      type: 'SET_ERROR',
      payload: { error },
    });
  }, []);

  const clearError = useCallback((): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const clearHistory = useCallback((): void => {
    dispatch({ type: 'CLEAR_HISTORY' });
  }, []);

  const loadMessages = useCallback((messages: ChatMessage[]): void => {
    dispatch({
      type: 'LOAD_MESSAGES',
      payload: { messages },
    });
  }, []);

  // Get messages formatted for LLM context
  // Filter out: system messages, tool messages (transient), and empty assistant messages
  const getMessagesForLLM = useCallback((): ChatMessage[] => {
    return state.messages.filter((msg) => {
      // Exclude system messages
      if (msg.role === 'system') return false;
      // Exclude tool messages (they become orphaned after tool processing)
      if (msg.role === 'tool') return false;
      // Exclude assistant messages with empty content (cleared intermediate messages)
      if (msg.role === 'assistant' && !msg.content && !msg.isStreaming) return false;
      return true;
    });
  }, [state.messages]);

  return useMemo(
    () => ({
      state,
      addUserMessage,
      startStreaming,
      appendToken,
      addToolCall,
      addToolResult,
      completeStreaming,
      clearMessageContent,
      setError,
      clearError,
      clearHistory,
      loadMessages,
      getMessagesForLLM,
    }),
    [
      state,
      addUserMessage,
      startStreaming,
      appendToken,
      addToolCall,
      addToolResult,
      completeStreaming,
      clearMessageContent,
      setError,
      clearError,
      clearHistory,
      loadMessages,
      getMessagesForLLM,
    ],
  );
};
